// ** Root Slice & Helper Functions
import { baseApi, customErrorHandler, TAGS } from '@/shared/api'

// ** Type Imports
import {
	type AssignCustomPriceVariables,
	type CreateCustomPriceVariables,
	type CustomPrice,
	type CustomPricingActionResponse,
	type GetCustomPricesParams,
	type MyPlan
} from '../model/types'

const CUSTOM_PRICING_URL = 'subscriptions/custom-pricing'

export const customPricingApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getCustomPrices: build.query<CustomPrice[], GetCustomPricesParams>({
			query: ({ includeArchived }) => ({
				url: CUSTOM_PRICING_URL,
				method: 'GET',
				params: { includeArchived }
			}),
			providesTags: [TAGS.CUSTOM_PRICES],
			transformErrorResponse: customErrorHandler
		}),
		/**
		 * The signed-in user's own plan. Any authenticated user, no active
		 * subscription required, so the pre-checkout screens can use it too.
		 */
		getMyPlan: build.query<MyPlan, void>({
			query: () => ({
				url: 'subscriptions/my-plan',
				method: 'GET'
			}),
			providesTags: [TAGS.MY_PLAN],
			transformErrorResponse: customErrorHandler
		}),
		getCustomPrice: build.query<CustomPrice, { priceId: string }>({
			query: ({ priceId }) => ({
				url: `${CUSTOM_PRICING_URL}/${priceId}`,
				method: 'GET'
			}),
			providesTags: [TAGS.CUSTOM_PRICES],
			transformErrorResponse: customErrorHandler
		}),
		createCustomPrice: build.mutation<CustomPrice, CreateCustomPriceVariables>({
			query: (body) => ({
				url: CUSTOM_PRICING_URL,
				method: 'POST',
				body
			}),
			transformErrorResponse: customErrorHandler,
			// The list endpoint is backed by Stripe search, which is eventually consistent
			// (it can take up to a minute for a new price to show up). So the response is
			// patched into the cached list instead of invalidating and refetching it.
			onQueryStarted: async (_variables, { dispatch, queryFulfilled }) => {
				try {
					const { data: createdPrice } = await queryFulfilled

					;[true, false].forEach((includeArchived) => {
						dispatch(
							customPricingApi.util.updateQueryData(
								'getCustomPrices',
								{ includeArchived },
								(draft) => {
									draft.unshift(createdPrice)
								}
							)
						)
					})
				} catch {
					// the global error middleware surfaces the failure to the user
				}
			}
		}),
		assignCustomPrice: build.mutation<
			CustomPricingActionResponse,
			AssignCustomPriceVariables
		>({
			query: ({ userId, ...body }) => ({
				url: `${CUSTOM_PRICING_URL}/assign/${userId}`,
				method: 'POST',
				body
			}),
			transformErrorResponse: customErrorHandler,
			// USERS refreshes the admin table and the target user's info; CUSTOM_PRICES
			// because a price that is now assigned can no longer be archived
			invalidatesTags: [TAGS.USERS, TAGS.ME, TAGS.CUSTOM_PRICES, TAGS.MY_PLAN]
		}),
		unassignCustomPrice: build.mutation<
			CustomPricingActionResponse,
			{ userId: number }
		>({
			query: ({ userId }) => ({
				url: `${CUSTOM_PRICING_URL}/assign/${userId}`,
				method: 'DELETE'
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.USERS, TAGS.ME, TAGS.CUSTOM_PRICES, TAGS.MY_PLAN]
		}),
		/**
		 * Cancels the target user's live subscription outright. Assignment is
		 * blocked while one exists, and cancelling at period end would not lift
		 * that block because Stripe keeps reporting `active` until it ends.
		 */
		cancelUserSubscription: build.mutation<
			CustomPricingActionResponse,
			{ userId: number }
		>({
			query: ({ userId }) => ({
				url: `${CUSTOM_PRICING_URL}/cancel-subscription/${userId}`,
				method: 'POST'
			}),
			transformErrorResponse: customErrorHandler,
			invalidatesTags: [TAGS.USERS, TAGS.ME, TAGS.MY_PLAN]
		}),
		archiveCustomPrice: build.mutation<
			CustomPricingActionResponse,
			{ priceId: string }
		>({
			query: ({ priceId }) => ({
				url: `${CUSTOM_PRICING_URL}/${priceId}`,
				method: 'DELETE'
			}),
			transformErrorResponse: customErrorHandler,
			// Patched locally for the same eventual-consistency reason as create.
			onQueryStarted: async ({ priceId }, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled

					dispatch(
						customPricingApi.util.updateQueryData(
							'getCustomPrices',
							{ includeArchived: true },
							(draft) => {
								const archivedPrice = draft.find(({ id }) => id === priceId)
								if (archivedPrice) archivedPrice.active = false
							}
						)
					)
					dispatch(
						customPricingApi.util.updateQueryData(
							'getCustomPrices',
							{ includeArchived: false },
							(draft) => {
								const index = draft.findIndex(({ id }) => id === priceId)
								if (index !== -1) draft.splice(index, 1)
							}
						)
					)
				} catch {
					// the global error middleware surfaces the failure to the user
				}
			}
		})
	})
})

export const {
	useGetCustomPricesQuery,
	useGetCustomPriceQuery,
	useGetMyPlanQuery,
	useCreateCustomPriceMutation,
	useArchiveCustomPriceMutation,
	useAssignCustomPriceMutation,
	useUnassignCustomPriceMutation,
	useCancelUserSubscriptionMutation
} = customPricingApi
